/**
 * Device pairing / runtime configuration.
 *
 * Resolution order for the kiosk key: `?key=` URL param (persisted, then stripped from the URL)
 * → localStorage → setup screen. The API URL may be overridden with `?api=` (also persisted),
 * otherwise `VITE_API_URL`, otherwise port 4000 on the host serving this page.
 *
 * `?preview=<token>` (from the owner dashboard) opens a read-only live view of a kiosk's screen.
 * It is never persisted, so it can't disturb a paired kiosk browser.
 */

const KEY_STORAGE = 'kiosk-display:key';
const API_STORAGE = 'kiosk-display:api-url';
const DEFAULT_API_URL = `${window.location.protocol}//${window.location.hostname}:4000`;

export interface DisplayConfig {
  apiUrl: string;
  kioskKey: string | null;
}

function safeGet(name: string): string | null {
  try {
    return localStorage.getItem(name);
  } catch {
    return null;
  }
}

function safeSet(name: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(name);
    else localStorage.setItem(name, value);
  } catch {
    // Storage unavailable (private mode / blocked): config lives for this page load only.
  }
}

/** Trims and removes trailing slashes; returns null for anything that isn't an http(s) URL. */
export function normalizeApiUrl(raw: string | null | undefined): string | null {
  const value = raw?.trim();
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return `${url.origin}${url.pathname}`.replace(/\/+$/, '');
  } catch {
    return null;
  }
}

function defaultApiUrl(): string {
  return normalizeApiUrl(import.meta.env.VITE_API_URL) ?? DEFAULT_API_URL;
}

// Values that only live in memory if storage is unavailable.
let memoryKey: string | null = null;
let memoryApi: string | null = null;

/**
 * Reads `?key=` / `?api=` from the URL, persists them and strips them from the address bar so the
 * key doesn't linger in history. Call once before rendering.
 */
export function bootstrapConfigFromUrl(): void {
  const url = new URL(window.location.href);
  let changed = false;

  const key = url.searchParams.get('key')?.trim();
  if (url.searchParams.has('key')) {
    if (key) saveKioskKey(key);
    url.searchParams.delete('key');
    changed = true;
  }

  if (url.searchParams.has('api')) {
    const api = normalizeApiUrl(url.searchParams.get('api'));
    if (api) saveApiUrl(api);
    url.searchParams.delete('api');
    changed = true;
  }

  if (changed) {
    const search = url.searchParams.toString();
    window.history.replaceState(null, '', `${url.pathname}${search ? `?${search}` : ''}${url.hash}`);
  }
}

export function loadConfig(): DisplayConfig {
  return {
    apiUrl: safeGet(API_STORAGE) ?? memoryApi ?? defaultApiUrl(),
    kioskKey: safeGet(KEY_STORAGE) ?? memoryKey,
  };
}

export function saveKioskKey(key: string): void {
  memoryKey = key.trim();
  safeSet(KEY_STORAGE, memoryKey);
}

/** Persists an API URL override; pass null to fall back to the build-time default. */
export function saveApiUrl(apiUrl: string | null): void {
  memoryApi = apiUrl;
  safeSet(API_STORAGE, apiUrl);
}

/** Forgets the kiosk key and API override (re-pairing). */
export function clearPairing(): void {
  memoryKey = null;
  memoryApi = null;
  safeSet(KEY_STORAGE, null);
  safeSet(API_STORAGE, null);
}

/** Owner preview token from the URL, if this page was opened as a read-only live view. */
export function readPreviewToken(): string | null {
  return new URL(window.location.href).searchParams.get('preview')?.trim() || null;
}

export { defaultApiUrl };
